<template>
  <v-container fluid>
    <div class="d-flex justify-space-between">
      <h3>{{ $t(reportObject.name) }}</h3>
      <div v-if="reportLoaded">
        <vf-tooltip :text="$t('save')">
          <v-btn
            v-if="reportObject.results_generated"
            fab text large
            color="primary"
            @click="toggleSave">
            <v-icon v-if="reportObject.status === 'saved'">mdi-star</v-icon>
            <v-icon v-else>mdi-star-outline</v-icon>
          </v-btn>
        </vf-tooltip>
        <vf-tooltip :text="$t('download')">
          <v-btn
            v-if="reportObject.downloadable && reportObject.results_generated"
            fab text large
            color="primary"
            @click="downloadReport"
            :loading="isDownloading">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <div v-if="!reportLoaded" class="mt-5 mb-10">
      <h5>{{ $t('reports_ui.loading_report' )}}</h5>
      <v-progress-linear color="primary" indeterminate/>
    </div>
    <div v-else>
      <div v-if="reportObject.status === 'error'" class="mt-3 mb-4">
        <v-alert type="danger" border="top" class="pb-1">
          <h4>{{ $t('reports_ui.error_generating_report') }}</h4>
          <p>{{ $t('reports_ui.error_generating_report_message') }}</p>
        </v-alert>
      </div>
      <component v-else v-bind:is="reportObject.type || ''" :report="reportObject" />
    </div>
    <v-card elevation="3">
      <v-card-title>
        {{ $t('reports_ui.report_parameters')}}
      </v-card-title>
      <v-card-text>
        <component
          ref="report-form"
          v-if="reportLoaded"
          v-bind:is="reportForm"
          :report="reportObject"
          context="update"
          v-on:report-saved="refreshTo"/>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text color="grey darken-2" @click="duplicateReport" :loading="duplicating">
          <v-icon>mdi-content-duplicate</v-icon>
          {{ $t('reports_ui.save_as_new_report') }}
        </v-btn>
        <v-btn text color="primary" @click="updateReport" :loading="updating">
          <v-icon>mdi-update</v-icon>
          {{ $t('reports_ui.update_report') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import VfTooltip from '@/components/shared/VfTooltip'
import VehicleViolationReport from '@/components/report/VehicleViolationReport'
import VehicleViolationReportForm from '@/components/report/VehicleViolationReportForm'
import StartTemperaturesReport from '@/components/report/StartTemperaturesReport'
import StartTemperaturesReportForm from '@/components/report/StartTemperaturesReportForm'
import MissedStopsReport from '@/components/report/MissedStopsReport'
import MissedStopsReportForm from '@/components/report/MissedStopsReportForm'
import SiteViolationReport from '@/components/report/SiteViolationReport'
import SiteViolationReportForm from '@/components/report/SiteViolationReportForm'
import MonthlyRouteStatusReport from '@/components/report/MonthlyRouteStatusReport'
import MonthlyRouteStatusReportForm from '@/components/report/MonthlyRouteStatusReportForm'
import RootCauseReport from '@/components/report/RootCauseReport'
import RootCauseReportForm from '@/components/report/RootCauseReportForm'
import RCAReport from '@/components/report/RCAReport'
import RCAReportForm from '@/components/report/RCAReportForm'
import CustomerMetricsReport from '@/components/report/CustomerMetricsReport'
import CustomerMetricsReportForm from '@/components/report/CustomerMetricsReportForm'
import RouteDataQualityReport from '@/components/report/RouteDataQualityReport'
import RouteDataQualityReportForm from '@/components/report/RouteDataQualityReportForm'
import WeeklyPerformanceReport from '@/components/report/WeeklyPerformanceReport'
import WeeklyPerformanceReportForm from '@/components/report/WeeklyPerformanceReportForm'
import WeeklyScorecardReport from '@/components/report/WeeklyScorecardReport'
import WeeklyScorecardReportForm from '@/components/report/WeeklyScorecardReportForm'
import FleetHealthReport from '@/components/report/fleet-health/FleetHealthReport'
import FleetHealthReportForm from '@/components/report/fleet-health/FleetHealthReportForm'
import RouteAssessmentMetricsReport from '@/components/report/route-assessment-metrics/RouteAssessmentMetricsReport'
import RouteAssessmentMetricsReportForm from '@/components/report/route-assessment-metrics/RouteAssessmentMetricsReportForm'
import FleetHealthHistoryReport from '@/components/report/performance-dashboard/FleetHealthHistoryReport'
import FleetHealthHistoryReportForm from '@/components/report/performance-dashboard/FleetHealthHistoryReportForm'
import TemperatureViolationHistoryReport from '@/components/report/performance-dashboard/TemperatureViolationHistoryReport'
import TemperatureViolationHistoryReportForm from '@/components/report/performance-dashboard/TemperatureViolationHistoryReportForm'
import RoutePlanHistoryReport from '@/components/report/performance-dashboard/RoutePlanHistoryReport'
import RoutePlanHistoryReportForm from '@/components/report/performance-dashboard/RoutePlanHistoryReportForm'
import RootCauseAnalysisHistoryReport from '@/components/report/performance-dashboard/RootCauseAnalysisHistoryReport'
import RootCauseAnalysisHistoryReportForm from '@/components/report/performance-dashboard/RootCauseAnalysisHistoryReportForm'
import StartTempLogReport from '@/components/report/StartTempLogReport.vue'
import StartTempLogReportForm from '@/components/report/StartTempLogReportForm.vue'
import VehicleCountReport from '@/components/report/VehicleCountReport.vue'
import VehicleCountReportForm from '@/components/report/VehicleCountReportForm.vue'
import ForecastViolationValidationReport from '@/components/report/ForecastViolationValidationReport'
import ForecastViolationValidationReportForm from '@/components/report/ForecastViolationValidationReportForm'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'

import FileSaver from 'file-saver'

export default {
  components: {
    VfTooltip,
    VehicleViolationReport,
    VehicleViolationReportForm,
    StartTemperaturesReport,
    StartTemperaturesReportForm,
    MissedStopsReport,
    MissedStopsReportForm,
    SiteViolationReport,
    SiteViolationReportForm,
    MonthlyRouteStatusReport,
    MonthlyRouteStatusReportForm,
    RootCauseReport,
    RootCauseReportForm,
    CustomerMetricsReport,
    CustomerMetricsReportForm,
    RouteDataQualityReport,
    RouteDataQualityReportForm,
    RCAReport,
    RCAReportForm,
    WeeklyPerformanceReport,
    WeeklyPerformanceReportForm,
    WeeklyScorecardReport,
    WeeklyScorecardReportForm,
    FleetHealthReport,
    FleetHealthReportForm,
    RouteAssessmentMetricsReport,
    RouteAssessmentMetricsReportForm,
    FleetHealthHistoryReport,
    FleetHealthHistoryReportForm,
    TemperatureViolationHistoryReport,
    TemperatureViolationHistoryReportForm,
    RoutePlanHistoryReport,
    RoutePlanHistoryReportForm,
    RootCauseAnalysisHistoryReport,
    RootCauseAnalysisHistoryReportForm,
    StartTempLogReport,
    StartTempLogReportForm,
    VehicleCountReport,
    VehicleCountReportForm,
    ForecastViolationValidationReport,
    ForecastViolationValidationReportForm
  },
  mixins: [
    BrowserUtilitiesMixin,
    CurrentUserMixin,
    ApiUtilitiesMixin
  ],
  data () {
    return {
      report: null,
      waitingBanner: null,
      duplicating: false,
      updating: false,
      isDownloading: false
    }
  },
  computed: {
    /**
     * Identifies if the report is loaded
     *
     * @return boolean
     */
    reportLoaded () {
      return this.report && this.report.results_generated != null
    },

    /**
     * Convenience function to ensure that we always have an object (and avoid "Cannot call ... on undefined"
     * exceptions)
     *
     * @return Object
     */
    reportObject () {
      return this.report || {}
    },

    /**
     * Returns the name of the form component to use to render this report
     *
     * @return string
     */
    reportForm () {
      return `${this.reportObject.type}Form`
    },

    /**
     * @return int
     */
    reportId () {
      return parseInt(this.$route.params.id)
    }
  },
  created () {
    // Register the socket listener once per component instance. It only
    // handles the on-page auto-refresh case; if the user navigates away the
    // instance is destroyed (listener removed below) and socketIoModule's
    // 'socket_report:update' action takes over, showing a banner with a
    // link.
    this.$socket.client.on('report:update', this.onReportUpdate)
    this.init()
  },
  beforeDestroy () {
    this.$socket.client.off('report:update', this.onReportUpdate)
  },
  watch: {
    /**
     * The report-view route is reused when only the :id param changes (e.g.
     * clicking a "report ready" banner link while already viewing another
     * report), so created() does not re-run. Re-initialize for the new id.
     */
    reportId (newId, oldId) {
      if (newId !== oldId) {
        this.init()
      }
    }
  },
  methods: {
    /**
     * Starts watching this report over the socket layer and kicks off
     * fetchReport()
     *
     * @return undefined
     */
    init () {
      // Watch this report: joins its socket room and tracks the id so the
      // room is re-joined after socket reconnects (VF-3266). Dispatched on
      // every init on purpose — regenerating a report needs to re-arm the
      // watch that 'socket_report:update' cleared.
      this.$store.dispatch('socketIoModule/watchReport', this.reportId)
      this.report = null
      this.duplicating = false
      this.updating = false
      this.fetchReport()
    },

    /**
     * Handles report:update socket events for the report this component is
     * currently displaying. Events for other reports are ignored here —
     * unlike the previous .once() listener, which any report's event could
     * consume — because socketIoModule's 'socket_report:update' action takes
     * care of announcing reports the user is not currently viewing.
     *
     * @param object payload socket event payload ({ report_id: int })
     * @return undefined
     */
    onReportUpdate (payload) {
      if (payload && payload.report_id === this.reportId) {
        this.fetchReport()
      }
    },

    /**
     * Initiate the update report process in the form subcomponent.
     */
    async updateReport () {
      this.updating = true
      await this.$refs['report-form'].updateReport()
      this.updating = false
    },

    /**
     * Initiate the duplicate report process in the form subcomponent.
     */
    async duplicateReport () {
      this.duplicating = true
      await this.$refs['report-form'].duplicateReport()
      this.duplicating = false
    },

    /**
     * Fetches the report data and if it's not ready then renders a banner.
     * If it is ready then updates internal state and sets the page title
     *
     * @return undefined
     */
    async fetchReport () {
      this.report = await this.$orm('Report').find(this.reportId)

      if (this.report.results_generated) {
        // Now that the report is rendered, we no longer need to watch for updates.
        this.$store.dispatch('socketIoModule/unwatchReport', this.reportId)
        if (this.waitingBanner) {
          await this.$store.dispatch('globalMessagingModule/removeBanner', this.waitingBanner)
          this.waitingBanner = null
        }

        // If the report hasn't been viewed yet, then update the status indicating that it's been viewed.
        if (this.report.status === 'ready') {
          this.report.status = 'viewed'
          this.$orm('Report').createOrUpdate(this.report)
        }
      } else {
        // If the report isn't ready, show a banner letting the user know it is being generated. When it finishes,
        // onReportUpdate (registered in created) refreshes this page if the user is still here; if they navigated
        // away, socketIoModule's 'socket_report:update' action shows a persistent banner with a link instead.
        // Re-arm the watch first: idempotent on a first load, and it keeps "waiting banner shown" in lockstep
        // with "room joined" if a completion event raced ahead of this fetch's read.
        this.$store.dispatch('socketIoModule/watchReport', this.reportId)
        if (this.waitingBanner) {
          await this.$store.dispatch('globalMessagingModule/removeBanner', this.waitingBanner)
        }
        this.waitingBanner = await this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          this.$t('awaiting_report')
        )
      }
      this.updateBrowserTitle(this.report.name)
    },

    /**
     * Executes an XHR request to fetch a csv report of the current search
     * criteria
     *
     * @return null
     */
    downloadReport () {
      // Update the UI to show the spinner on the download button
      this.isDownloading = true

      // Fire an analytics event
      this.$gtag.event('download', { event_category: 'Report' })

      this.$httpAdapter({
        url: this.$orm('Report').endpoint + `/${this.report.id}`,
        method: 'GET',
        headers: {
          Accept: 'text/csv'
        },
        // important
        responseType: 'blob'
      }).then((response) => {
        // Parse the file name from the content-disposition header
        const headerLine = response.headers['content-disposition']
        const startFileNameIndex = headerLine.indexOf('"') + 1
        const endFileNameIndex = headerLine.lastIndexOf('"')
        const fileName = headerLine.substring(startFileNameIndex, endFileNameIndex)
        FileSaver.saveAs(new Blob([response.data]), fileName)
      })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.isDownloading = false
        })
    },

    async toggleSave () {
      this.report.status = this.report.status === 'viewed' ? 'saved' : 'viewed'
      const message = this.report.status === 'saved' ? 'favorited_report' : 'unfavorited_report'
      await this.$orm('Report').createOrUpdate(this.report)
      await this.$store.dispatch('globalMessagingModule/showSuccessFlashBanner', this.$t(message))
    },

    /**
     * If a new report has been generated then push to its route — the
     * reportId watcher reinitializes the component once the route change
     * lands. Otherwise (same report updated in place) reinitialize directly.
     *
     * @reutrn undefined
     */
    refreshTo (report) {
      if (report.id !== this.report.id) {
        this.$router.push(`/report/${report.id}`)
      } else {
        this.init()
      }
    }
  }
}

</script>
